/**
 * ======================================================
 * OPENROUTER AI SERVICE - FRONTEND VERSION
 * ======================================================
 * Đây là service dành riêng cho frontend để gọi OpenRouter API.
 * Service này chỉ được sử dụng ở frontend (không phải admin).
 *
 * Cách sử dụng:
 * import { handleAIRequest } from '@/services/openRouterAIService';
 *
 * LƯU Ý: File này thay thế cho aiService.mjs cũ. Hãy cập nhật
 * tất cả các imports từ aiService.mjs sang openRouterAIService.mjs.
 * ======================================================
 */

import axios from "axios";
import { supabase } from "../lib/supabaseClient";
import * as openRouterService from "./openRouterService.js";

// Lấy API key và URL từ biến môi trường
const CHAT_API_KEY =
  process.env.NEXT_PUBLIC_CHAT_API_KEY || "app-rFr7B6vcppZeKTCKLjcPDviE";
const CHAT_API_URL =
  process.env.NEXT_PUBLIC_CHAT_API_URL ||
  "https://ai.dreamapi.net/v1/chat-messages";

// Biến môi trường để điều khiển việc sử dụng OpenRouter
const USE_OPENROUTER =
  import.meta.env.VITE_USE_OPENROUTER === "true" ||
  process.env.USE_OPENROUTER === "true";

// Kiểm tra debug mode
const DEBUG_MODE =
  import.meta.env.VITE_DEBUG === "true" || process.env.DEBUG === "true";

// Cache cho system config
let systemConfig = null;
let lastConfigFetch = 0;
const CONFIG_CACHE_DURATION = 5 * 60 * 1000; // 5 phút

/**
 * Lấy system config từ database hoặc cache
 * @returns {Promise<Object>} - System config
 */
async function getSystemConfig() {
  const now = Date.now();

  // Sử dụng cache nếu có và chưa hết hạn
  if (systemConfig && now - lastConfigFetch < CONFIG_CACHE_DURATION) {
    return systemConfig;
  }

  try {
    // Không gọi Supabase nữa để tránh lỗi 404
    // Sử dụng cấu hình mặc định thay thế
    console.log("Sử dụng cấu hình mặc định cho AI settings");

    // Tạo cấu hình mặc định
    const defaultConfig = {
      aiSettings: {
        useOpenRouter: true, // Đảm bảo luôn sử dụng OpenRouter ở frontend
        defaultModel: "google/gemini-pro",
        chatModel: "google/gemini-pro",
        courseGenerationModel: "anthropic/claude-3-sonnet",
        contentGenerationModel: "anthropic/claude-3-haiku",
      },
    };

    // Lưu vào cache
    systemConfig = defaultConfig;
    lastConfigFetch = now;

    return defaultConfig;
  } catch (error) {
    console.error("Lỗi khi lấy system config:", error);

    // Nếu có lỗi, trả về cấu hình mặc định
    const defaultConfig = {
      aiSettings: {
        useOpenRouter: true, // Đảm bảo luôn sử dụng OpenRouter ở frontend
        defaultModel: "google/gemini-pro",
        chatModel: "google/gemini-pro",
        courseGenerationModel: "anthropic/claude-3-sonnet",
        contentGenerationModel: "anthropic/claude-3-haiku",
      },
    };

    return defaultConfig;
  }
}

/**
 * Kiểm tra xem có nên sử dụng OpenRouter không
 * @returns {Promise<boolean>} - Có sử dụng OpenRouter không
 */
async function shouldUseOpenRouter() {
  try {
    // Cho phép biến môi trường ghi đè
    const envValue =
      import.meta.env.VITE_USE_OPENROUTER === "true" ||
      process.env.USE_OPENROUTER === "true";

    // Kiểm tra xem đang ở trang admin không
    const isAdminPage =
      typeof window !== "undefined" &&
      (window.location.pathname.includes("/admin") ||
        window.location.href.includes("/admin"));

    // Debug thông tin
    console.log("Thông tin UsedOpenRouter:", {
      envValue,
      isAdminPage,
      pathname:
        typeof window !== "undefined" ? window.location.pathname : "undefined",
      href: typeof window !== "undefined" ? window.location.href : "undefined",
    });

    // Nếu đang ở trang admin, không sử dụng OpenRouter
    if (isAdminPage) {
      console.log("Đang ở trang admin, sử dụng Dify API thay vì OpenRouter");
      return false;
    }

    // Chat widget hoặc dashboard chính - luôn dùng OpenRouter
    const isChatWidget =
      typeof window !== "undefined" &&
      (window.location.pathname.includes("/courses/") ||
        window.location.pathname === "/" ||
        window.location.href.includes("/chat"));

    if (isChatWidget) {
      console.log(
        "Đang ở ChatWidget hoặc trang học, bắt buộc sử dụng OpenRouter",
      );
      return true;
    }

    // Ưu tiên biến môi trường
    if (envValue) {
      // Thử lấy config từ database để kiểm tra
      try {
        const config = await getSystemConfig();
        // Nếu lấy được config, sử dụng giá trị trong đó
        if (config && config.aiSettings) {
          return config.aiSettings.useOpenRouter === true;
        }
        // Nếu không lấy được config, sử dụng giá trị từ env
        return true;
      } catch (error) {
        console.warn("Không thể lấy cấu hình từ database, sử dụng Dify API");
        return false;
      }
    }

    return false;
  } catch (error) {
    console.error("Lỗi khi kiểm tra cấu hình OpenRouter:", error);
    // Khi gặp lỗi, mặc định sẽ sử dụng Dify API
    return false;
  }
}

/**
 * Lấy model mặc định cho service type
 * @param {string} serviceType - Loại service (chat, course_generation, content_generation)
 * @returns {Promise<string>} - ID của model
 */
async function getDefaultModel(serviceType) {
  try {
    const config = await getSystemConfig();

    // Nếu không có config, trả về model mặc định
    if (!config?.aiSettings) {
      console.warn(
        `Không tìm thấy aiSettings, sử dụng model mặc định cho ${serviceType}`,
      );
      return getDefaultModelByType(serviceType);
    }

    switch (serviceType) {
      case "chat":
        return (
          config.aiSettings.chatModel ||
          config.aiSettings.defaultModel ||
          getDefaultModelByType(serviceType)
        );
      case "course_generation":
        return (
          config.aiSettings.courseGenerationModel ||
          config.aiSettings.defaultModel ||
          getDefaultModelByType(serviceType)
        );
      case "content_generation":
        return (
          config.aiSettings.contentGenerationModel ||
          config.aiSettings.defaultModel ||
          getDefaultModelByType(serviceType)
        );
      default:
        return (
          config.aiSettings.defaultModel || getDefaultModelByType(serviceType)
        );
    }
  } catch (error) {
    console.error(`Lỗi khi lấy model mặc định cho ${serviceType}:`, error);
    return getDefaultModelByType(serviceType);
  }
}

/**
 * Trả về model mặc định dựa trên loại service
 * @param {string} serviceType - Loại service
 * @returns {string} - ID của model mặc định
 */
function getDefaultModelByType(serviceType) {
  switch (serviceType) {
    case "chat":
      return "google/gemini-2.0-flash-001";
    case "course_generation":
      return "google/gemini-2.0-flash-001";
    case "content_generation":
      return "google/gemini-2.0-flash-001";
    default:
      return "google/gemini-2.0-flash-001";
  }
}

/**
 * Lấy OpenRouter API key từ cấu hình
 * @returns {Promise<string>} - API key
 */
async function getOpenRouterApiKey() {
  try {
    // Ưu tiên biến môi trường
    const envApiKey =
      import.meta.env.VITE_OPENROUTER_API_KEY || process.env.OPENROUTER_API_KEY;
    if (envApiKey) {
      return envApiKey;
    }

    // Nếu không có trong biến môi trường, lấy từ config
    const config = await getSystemConfig();
    return config?.aiSettings?.openRouterApiKey || "";
  } catch (error) {
    console.error("Lỗi khi lấy OpenRouter API key:", error);
    return "";
  }
}

// Hàm lấy hoặc tạo user ID
const getUserId = () => {
  // Kiểm tra xem đã có user ID trong localStorage chưa
  const storedUserId =
    typeof window !== "undefined" ? localStorage.getItem("ai_user_id") : null;

  if (storedUserId) {
    return storedUserId;
  }

  // Nếu chưa có, tạo mới và lưu vào localStorage
  const newUserId = "user-" + Math.random().toString(36).substring(2, 15);

  if (typeof window !== "undefined") {
    localStorage.setItem("ai_user_id", newUserId);
  }

  return newUserId;
};

// Hàm kiểm tra JSON hợp lệ
function isValidJSON(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

// Hàm chuẩn hóa tham số cho API
function sanitizeDifyInputs(options = {}) {
  const data = {
    service_type: options.service_type || "chat",
    max_tokens: options.max_tokens || 1000,
    temperature: options.temperature || 0.7,
  };

  // Thêm các tham số hợp lệ khác
  if (options.expert_id) data.expert_id = options.expert_id;
  if (options.lesson_name) data.lesson_name = options.lesson_name;
  if (options.course_id) data.course_id = options.course_id;

  // Chat history - cần đảm bảo là JSON string hợp lệ
  if (options.chat_history) {
    // Nếu đã là string, kiểm tra tính hợp lệ
    if (typeof options.chat_history === "string") {
      if (isValidJSON(options.chat_history)) {
        data.chat_history = options.chat_history;
      } else {
        console.warn("chat_history không phải là JSON hợp lệ, bỏ qua");
      }
    }
    // Nếu là array, chuyển thành JSON string
    else if (Array.isArray(options.chat_history)) {
      data.chat_history = JSON.stringify(options.chat_history);
    }
  }

  return data;
}

/**
 * Gửi request đến API AI và nhận phản hồi
 * @param {string} prompt - Nội dung prompt gửi đến AI
 * @param {Object} options - Các tùy chọn bổ sung
 * @returns {Promise<Object>} - Đối tượng chứa phản hồi từ AI và ID cuộc hội thoại
 */
export async function handleAIRequest(prompt, options = {}) {
  // Kiểm tra có sử dụng OpenRouter không và đang ở frontend
  const useOpenRouter = await shouldUseOpenRouter();

  // Frontend sử dụng OpenRouter
  if (typeof window !== "undefined" && useOpenRouter) {
    console.log("Đang sử dụng OpenRouter trên frontend...");
    try {
      // Thêm model mặc định cho chat nếu không được chỉ định
      if (!options.model) {
        const defaultModel = await getDefaultModel("chat");
        options.model = defaultModel;
        console.log(`Sử dụng model mặc định cho chat: ${defaultModel}`);
      }

      // Lấy API key từ cấu hình nếu cần
      const apiKey = await getOpenRouterApiKey();
      if (apiKey && apiKey !== openRouterService.getApiKey()) {
        openRouterService.setApiKey(apiKey);
      }

      try {
        // Tạo ID hội thoại nếu chưa có
        const conversationId =
          options.conversation_id ||
          `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

        // Gọi OpenRouter và nhận phản hồi
        const response = await openRouterService.handleOpenRouterRequest(
          prompt,
          options,
        );

        // Kiểm tra và định dạng phản hồi
        if (typeof response === "string") {
          // Nếu phản hồi là chuỗi đơn giản, trả về với định dạng chuẩn
          return {
            answer: response,
            conversation_id: conversationId,
          };
        } else if (response && typeof response === "object") {
          // Nếu phản hồi đã là object, đảm bảo nó có các trường cần thiết
          if (response.answer) {
            return {
              answer: response.answer,
              conversation_id: response.conversation_id || conversationId,
            };
          } else {
            // Tìm trong các trường phổ biến nếu không có answer
            for (const key of ["content", "message", "text", "response"]) {
              if (response[key]) {
                return {
                  answer: response[key],
                  conversation_id: response.conversation_id || conversationId,
                };
              }
            }

            // Nếu không tìm thấy giá trị trong các trường, chuyển đổi toàn bộ đối tượng thành chuỗi
            console.warn(
              "Không tìm thấy trường answer trong phản hồi, chuyển đổi toàn bộ đối tượng",
              response,
            );
            return {
              answer: JSON.stringify(response),
              conversation_id: conversationId,
            };
          }
        }

        // Trường hợp không xác định, trả về thông báo lỗi
        console.warn("Phản hồi từ OpenRouter không đúng định dạng", response);
        return {
          answer: "Phản hồi từ AI không đúng định dạng. Vui lòng thử lại.",
          conversation_id: conversationId,
        };
      } catch (openRouterError) {
        console.error("Lỗi khi gọi OpenRouter, chi tiết:", openRouterError);
        console.log("Sử dụng phương thức phản hồi tạm thời do lỗi kết nối");

        // Tạo ID hội thoại nếu chưa có
        const conversationId =
          options.conversation_id ||
          `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

        // Tạo câu trả lời tạm thời
        return {
          answer:
            "Xin lỗi, tôi không thể kết nối đến dịch vụ AI. Lỗi kết nối đang xảy ra với server OpenRouter. Vui lòng kiểm tra:\n\n1. Máy chủ AI Proxy (http://localhost:3005) đã được khởi động chưa\n2. OPENROUTER_API_KEY đã được cấu hình trong file .env chưa\n\nBạn có thể thử lại sau hoặc liên hệ với quản trị viên để được hỗ trợ.",
          conversation_id: conversationId,
        };
      }
    } catch (error) {
      console.error("Lỗi khi sử dụng OpenRouter:", error);
      console.log("Chuyển sang sử dụng Dify API do lỗi OpenRouter");
      // Tiếp tục với Dify API nếu OpenRouter gặp lỗi
    }
  }

  // Backend sử dụng Dify API hoặc câu trả lời tạm thời nếu có lỗi
  console.log("Đang sử dụng Dify API...");
  try {
    // Chuẩn bị data cho request theo đúng định dạng trong tài liệu API
    const conversationId = options.conversation_id || "";

    // Chuẩn bị input data, bao gồm cả system prompt nếu có
    const inputData = {
      service_type: options.service_type || "chat",
      max_tokens: options.max_tokens || 500,
      temperature: options.temperature || 0.7,
    };

    // Thêm system prompt nếu có
    if (options.system_prompt) {
      inputData.system_prompt = options.system_prompt;
    }

    const requestData = {
      query: prompt,
      user: options.user_id || getUserId(),
      conversation_id: conversationId,
      response_mode: "blocking",
      inputs: {
        data: JSON.stringify(inputData),
      },
    };

    console.log("Đang gửi request tới API AI...", { url: CHAT_API_URL });

    // Gửi request đến API với timeout tăng lên và thiết lập CORS
    const response = await axios.post(CHAT_API_URL, requestData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${CHAT_API_KEY}`,
      },
      timeout: 60000, // Tăng timeout lên 60 giây
      withCredentials: false, // Không gửi credentials để tránh lỗi CORS
    });

    // Kiểm tra và xử lý response
    if (response.data && response.data.answer) {
      // Lưu lại lịch sử sử dụng AI nếu cần
      if (options.track_usage !== false) {
        await trackAIUsage({
          user_id: options.user_id || getUserId(),
          prompt_length: prompt.length,
          response_length: response.data.answer.length,
          tokens_used: response.data.metadata?.usage?.total_tokens || 0,
          service_type: options.service_type || "chat",
        });
      }

      // Đảm bảo trả về conversation_id
      return {
        answer: response.data.answer,
        conversation_id:
          conversationId ||
          `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      };
    }

    throw new Error("Không nhận được kết quả từ AI");
  } catch (error) {
    console.error("Lỗi khi gọi API AI:", error);

    // Xử lý lỗi CORS
    if (error.message && error.message.includes("Network Error")) {
      console.error(
        "Lỗi kết nối mạng hoặc CORS - thử tạo request qua proxy/middleware",
      );

      // Thử sử dụng proxy thay thế nếu có
      try {
        // Nếu có backend proxy URL, thử gọi thông qua đó
        const PROXY_URL = "http://localhost:3005/api/ai-proxy"; // URL tới proxy endpoint trên backend

        if (typeof window !== "undefined") {
          const proxyRequestData = {
            prompt,
            data_info: JSON.stringify({
              service_type: options.service_type || "chat",
              temperature: options.temperature || 0.7,
              max_tokens: options.max_tokens || 500,
              conversation_id: conversationId,
              system_prompt: options.system_prompt || "",
            }),
          };

          const proxyResponse = await axios.post(PROXY_URL, proxyRequestData);

          if (proxyResponse.data && proxyResponse.data.answer) {
            return {
              answer: proxyResponse.data.answer,
              conversation_id:
                conversationId ||
                `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            };
          }
        }
      } catch (proxyError) {
        console.error("Lỗi khi gọi proxy:", proxyError);
      }
    }

    throw error;
  }
}

/**
 * Lấy danh sách cuộc trò chuyện
 * @returns {Array} - Danh sách cuộc trò chuyện
 */
export function getChatConversations() {
  try {
    if (typeof window === "undefined") return [];

    const storageKey = "chat_conversations_list";
    const cookieValue = getCookie(storageKey);

    if (cookieValue) {
      return JSON.parse(decodeURIComponent(cookieValue));
    }

    return [];
  } catch (error) {
    console.error("Lỗi khi lấy danh sách cuộc trò chuyện:", error);
    return [];
  }
}

/**
 * Lấy lịch sử chat từ database hoặc local storage
 * @returns {Promise<Array>} Mảng các phiên chat
 */
export const getChatHistory = async () => {
  // Mô phỏng lấy lịch sử chat từ local storage
  const chatHistoryString = localStorage.getItem('chatHistory');
  if (chatHistoryString) {
    try {
      return JSON.parse(chatHistoryString);
    } catch (error) {
      console.error('Error parsing chat history from localStorage:', error);
      return [];
    }
  }
  
  // Trả về mảng rỗng nếu không có lịch sử
  return [];
};

/**
 * Lưu lịch sử chat vào database hoặc local storage
 * @param {Array} history Mảng các phiên chat
 * @returns {Promise<void>}
 */
export const saveChatHistory = async (history) => {
  // Mô phỏng lưu lịch sử chat vào local storage
  try {
    localStorage.setItem('chatHistory', JSON.stringify(history));
  } catch (error) {
    console.error('Error saving chat history to localStorage:', error);
  }
};

/**
 * Tạo nội dung với AI
 * @param {string} prompt - Mô tả nội dung cần tạo
 * @param {Object} baseInfo - Thông tin cơ bản (tiêu đề, mô tả, etc)
 * @param {Object} templateInfo - Thông tin template (nếu có)
 * @returns {Promise<Object>} - Dữ liệu nội dung được tạo
 */
export async function generateContentWithAI(
  prompt,
  baseInfo = {},
  templateInfo = {},
) {
  try {
    // Kiểm tra có sử dụng OpenRouter không và đang ở frontend
    const useOpenRouter = await shouldUseOpenRouter();

    // Frontend sử dụng OpenRouter
    if (typeof window !== "undefined" && useOpenRouter) {
      console.log("Đang sử dụng OpenRouter trên frontend để tạo nội dung...");
      try {
        // Thêm model mặc định từ cấu hình
        const defaultModel = await getDefaultModel("content_generation");
        const options = { model: defaultModel };

        // Lấy API key từ cấu hình
        const apiKey = await getOpenRouterApiKey();
        if (apiKey && apiKey !== openRouterService.getApiKey()) {
          openRouterService.setApiKey(apiKey);
        }

        return await openRouterService.generateContentWithOpenRouter(
          prompt,
          baseInfo,
          templateInfo,
          options,
        );
      } catch (error) {
        console.error(
          "Lỗi khi sử dụng OpenRouter, chuyển sang sử dụng Dify API:",
          error,
        );
      }
    }

    // Backend hoặc fallback khi OpenRouter bị lỗi
    console.log("Đang sử dụng Dify API để tạo nội dung...");

    // Xây dựng prompt dựa trên template nếu có
    let enhancedPrompt = prompt;
    if (templateInfo && templateInfo.template) {
      enhancedPrompt = `Template: ${templateInfo.template}\n\nYêu cầu: ${prompt}`;
    }

    // Thêm các thông tin cơ bản vào prompt nếu có
    if (baseInfo) {
      if (baseInfo.title) enhancedPrompt += `\nTiêu đề: ${baseInfo.title}`;
      if (baseInfo.description)
        enhancedPrompt += `\nMô tả: ${baseInfo.description}`;
      if (baseInfo.category)
        enhancedPrompt += `\nDanh mục: ${baseInfo.category}`;
    }

    // Chuẩn hóa tham số inputs
    const inputData = sanitizeDifyInputs({
      service_type: "content_generation",
      max_tokens: 4000,
      temperature: 0.7,
    });

    // Chuẩn bị data cho request
    const requestData = {
      query: enhancedPrompt,
      user: getUserId(),
      conversation_id: "",
      response_mode: "blocking",
      inputs: {
        data: JSON.stringify(inputData),
      },
    };

    if (DEBUG_MODE) {
      console.log(
        "Dify request data (content generation):",
        JSON.stringify(requestData, null, 2),
      );
    }

    console.log("Đang gửi request tới Dify API để tạo nội dung...");

    // Gửi request đến API
    const response = await axios.post(CHAT_API_URL, requestData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${CHAT_API_KEY}`,
      },
      timeout: 120000, // Tăng timeout lên 120 giây vì tạo nội dung cần nhiều thời gian
      withCredentials: false,
    });

    // Kiểm tra và xử lý response
    if (response.data && response.data.answer) {
      // Theo dõi việc sử dụng AI
      await trackAIUsage({
        user_id: getUserId(),
        prompt_length: prompt.length,
        response_length: response.data.answer.length,
        tokens_used: response.data.metadata?.usage?.total_tokens || 0,
        service_type: "content_generation",
      });

      return {
        answer: response.data.answer,
        content: response.data.answer,
        metadata: response.data.metadata || {},
      };
    }

    throw new Error("Không nhận được kết quả từ AI");
  } catch (error) {
    console.error("Lỗi khi tạo nội dung với AI:", error);
    throw error;
  }
}

/**
 * Lấy giá trị từ cookie theo key
 * @param {string} name - Tên cookie
 * @returns {string|null} - Giá trị cookie
 */
function getCookie(name) {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

/**
 * Thiết lập cookie
 * @param {string} name - Tên cookie
 * @param {string} value - Giá trị cần lưu
 * @param {number} days - Số ngày tồn tại của cookie
 */
function setCookie(name, value, days = 30) {
  if (typeof document === "undefined") return;

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expiryDate.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Theo dõi việc sử dụng AI
 * @param {Object} usageData - Dữ liệu sử dụng
 */
async function trackAIUsage(usageData) {
  try {
    // Thay vì lưu vào database, chỉ in log thông tin
    console.log("Thông tin sử dụng AI:", {
      user_id: usageData.user_id,
      prompt_length: usageData.prompt_length || 0,
      response_length: usageData.response_length || 0,
      tokens_used: usageData.tokens_used || 0,
      service_type: usageData.service_type || "chat",
      timestamp: new Date().toISOString(),
    });

    // Lưu vào localStorage nếu đang ở môi trường browser
    if (typeof window !== "undefined") {
      try {
        const aiUsage = JSON.parse(localStorage.getItem("ai_usage") || "[]");
        aiUsage.push({
          user_id: usageData.user_id,
          prompt_length: usageData.prompt_length || 0,
          response_length: usageData.response_length || 0,
          tokens_used: usageData.tokens_used || 0,
          service_type: usageData.service_type || "chat",
          timestamp: new Date().toISOString(),
        });

        // Giới hạn lịch sử sử dụng để không quá lớn
        if (aiUsage.length > 100) {
          aiUsage.splice(0, aiUsage.length - 100);
        }

        localStorage.setItem("ai_usage", JSON.stringify(aiUsage));
      } catch (error) {
        console.error(
          "Lỗi khi lưu thông tin sử dụng AI vào localStorage:",
          error,
        );
      }
    }
  } catch (error) {
    console.error("Lỗi khi theo dõi sử dụng AI:", error);
  }
}

/**
 * Chat với AI thông qua ai-proxy.ts
 * @param {string} prompt - Tin nhắn của người dùng
 * @param {Object} options - Các tùy chọn cho AI (expert_id, content_info, lesson_name, course_id, conversation_id, chat_history)
 * @returns {Promise<Object>} - Phản hồi từ AI và ID cuộc hội thoại
 */
export async function chatWithAI(prompt, options = {}) {
  try {
    // Endpoint cho AI Proxy
    const AI_PROXY_URL = '/api/ai-proxy';
    
    console.log("Chat với AI thông qua ai-proxy:", {
      promptLength: prompt.length,
      hasOptions: !!options,
      expert_id: options.expert_id || "Không có",
      course_id: options.course_id || "Không có",
      conversation_id: options.conversation_id || "Không có",
      chatHistoryLength: options.chat_history?.length || 0,
    });
    
    // Chuẩn bị dữ liệu cho request, chỉ gửi các tham số cần thiết
    const requestData = {
      prompt: prompt,
      data_info: options.content_info || "",
      expert_id: options.expert_id || "",
      lesson_name: options.lessonName || "",
      course_id: options.course_id || "",
      chat_history: options.chat_history || [],
      conversation_id: options.conversation_id || ""
      // Không gửi api_type hoặc model_type
    };
    
    // Gọi đến API Proxy
    const response = await axios.post(AI_PROXY_URL, requestData, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 60000 // 60 giây timeout
    });
    
    // Xử lý phản hồi
    if (response.data && response.data.answer) {
      return {
        answer: response.data.answer,
        conversation_id: response.data.conversation_id || options.conversation_id || `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        message_id: `ai-${Date.now()}`
      };
    }
    
    throw new Error("Không nhận được phản hồi hợp lệ từ AI Proxy");
  } catch (error) {
    console.error("Lỗi khi chat với AI thông qua ai-proxy:", error);
    
    // Kiểm tra lỗi conversation not exists
    if (error.response?.status === 404 || 
        (error.message && error.message.toLowerCase().includes("conversation not exists"))) {
      throw new Error("Conversation not exists");
    }
    
    // Tạo phản hồi lỗi
    throw error;
  }
}

// Thêm export default
export default {
  handleAIRequest,
  generateContentWithAI,
  getChatConversations,
  getChatHistory,
  saveChatHistory,
  chatWithAI
};
